import debugObject from 'debug';
import unionBy from 'lodash/unionBy';
import differenceBy from 'lodash/differenceBy';
import {hasFileWithFakeExtension} from '../DNFORM-utils/DNFORM-utils';
import {deleteFile, uploadFiles} from '../DNFORM-utils/DNFORM-api';
import {ERROR_CODES, FILE_INPUT_STATES} from '../DNFORM-utils/DNFORM-constants';

const debug = debugObject('sf:dynaform:dnform-file-input-mixin');

export const fileInputMixin = {
	data() {
		return {
			FILE_INPUT_STATES,
			isUploading: {},
			uploadProgress: {},
			isDragOn: {},
			uploadedFiles: {}
		};
	},
	created() {
		// Set the init values of the file input states to make them accessible and reactive
		const endStep = this.isSummaryEnabled ? this.totalSteps - 1 : this.totalSteps;

		for (let step = 1; step <= endStep; step++) {
			Object.values(FILE_INPUT_STATES).forEach((state) => {
				this.$set(this[state], step, {});
			});
		}
	},
	methods: {
		// To set the isUploading, isDragOn, uploadProgress and uploadedFiles states of a specific file input
		setFileInputState(state, fileInputId, stateValue) {
			this.$set(this[state][this.currentStep], fileInputId, stateValue);
		},
		getFilesOfFileInput(fileInputId) {
			return this.model[this.currentStep][fileInputId] ? this.model[this.currentStep][fileInputId] : [];
		},
		setFilesOfFileInput(fileInputId, newFiles, includeCurrentFiles = true) {
			const currentFiles = includeCurrentFiles ? this.getFilesOfFileInput(fileInputId) : [];
			const totalFiles = this.replaceExistingFilesWithNew(newFiles, currentFiles);
			// Set the file input's model value to empty string If we deleted all files to make the 'required' validation rule fail
			this.model[this.currentStep][fileInputId] = totalFiles.length ? totalFiles : '';
		},
		removeFilesFromInput(fileInputId, filesToRemove) {
			const currentFiles = this.getFilesOfFileInput(fileInputId);
			const remainingFiles = differenceBy(currentFiles, Array.from(filesToRemove));
			this.model[this.currentStep][fileInputId] = remainingFiles.length ? remainingFiles : '';
		},
		replaceExistingFilesWithNew(newFiles, currentFiles) {
			return unionBy(Array.from(newFiles), Array.from(currentFiles), 'name');
		},
		onUploadProgress(fileInputId) {
			return progressEvent => {
				const progressInPercentage = Math.round(progressEvent.loaded / progressEvent.total * 100);
				this.setFileInputState(FILE_INPUT_STATES.UPLOAD_PROGRESS, fileInputId, progressInPercentage);
			};
		},
		uploadFiles(fileInputId, files) {
			this.setFileInputState(FILE_INPUT_STATES.IS_UPLOADING, fileInputId, true);
			const formData = new FormData();

			for (const file of files) {
				formData.append('files', file);
			}

			uploadFiles(this.formId, this.sessionToken, this.currentStep, fileInputId, formData, this.onUploadProgress)
				.then((response) => {
					if (response.data && response.data.length) {
						this.setFileInputState(FILE_INPUT_STATES.UPLOADED_FILES, fileInputId, response.data);
					} else if (response.data[this.currentStep] && response.data[this.currentStep].length) {
						this.handleErrorResponse(response.data);
					}
				})
				.catch((error) => {
					this.removeFilesFromInput(fileInputId, files);
					console.error('Failed to upload files', error, 'Params:',
						{formId: this.formId, sessionToken: this.sessionToken, pageNumber: this.currentStep, fileInputId, formData});
				})
				.finally(() => {
					this.setFileInputState(FILE_INPUT_STATES.IS_UPLOADING, fileInputId, false);
				});
		},
		deleteFile(fileInputId, filename) {
			deleteFile(this.formId, this.sessionToken, fileInputId, filename)
				.then((response) => {
					const remainingFileObjects = this.model[this.currentStep][fileInputId].filter((uploadedFile) => uploadedFile.name !== filename);
					this.setFilesOfFileInput(fileInputId, remainingFileObjects, false);
					this.setFileInputState(FILE_INPUT_STATES.UPLOADED_FILES, fileInputId, response.data);
					this.validateInput(fileInputId);
				})
				.catch((error) => {
					console.error('Failed to delete file', error, 'Params:', {formId: this.formId, sessionToken: this.sessionToken, fileInputId, filename});
				});
		},
		validateFiles(fileInputId, files) {
			debug('validateFiles', {fileInputId, files});
			// We should handle this with livr but it doesn't support async validation
			return hasFileWithFakeExtension(files)
				.then((hasError) => {
					debug('validateFiles then', {hasError});
					this.setFilesOfFileInput(fileInputId, files);
					this.validateInput(fileInputId);

					if (hasError) {
						this.errors.items[fileInputId] = ERROR_CODES.FORBIDDEN_FILE_EXTENSION;
						this.errors.touchedFields[fileInputId] = true;
					}
				})
				.catch((error) => {
					console.error('Failed to get result of hasFileWithFakeExtension', error, 'File input ID', fileInputId);
				});
		},
		handleFileUpload(fileInputId, files) {
			if (!this.errors.items[fileInputId]) {
				this.uploadFiles(fileInputId, files);
			} else {
				this.removeFilesFromInput(fileInputId, files);
			}
		},
		handleFileDrop(event, id) {
			const {files} = event.dataTransfer;
			this.setFileInputState(FILE_INPUT_STATES.IS_DRAG_ON, id, false);
			this.turnOnPageLeaveWarning();

			this.validateFiles(id, files)
				.then(() => this.handleFileUpload(id, files))
				.catch((error) => {
					console.error('Failed to validate files while handling file drop', error, 'File input ID', id);
				});
		},
		handleFileInput(event) {
			debug('handleFileInput');
			const {id, files} = event.target;
			this.turnOnPageLeaveWarning();

			this.validateFiles(id, files)
				.then(() => {
					debug('handleFileInput validateFiles then');
					this.handleFileUpload(id, files);
				})
				.catch((error) => {
					console.error('Failed to validate files while handling file input', error, 'File input ID', id);
				});
		}
	}
};



// WEBPACK FOOTER //
// ../source/modules/DNFORM/DNFORM-mixins/DNFORM-file-input-mixin.js